import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiCompass, FiMapPin, FiSearch } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { touristPlaceService } from '../services/touristPlaceService';
import { EmptyState, ErrorState, LoadingState, PageHero, SectionHeading } from '../components/ui/PageElements';

const TouristicPlacesPage = () => {
  const { t } = useTranslation();
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  useEffect(() => {
    const fetchPlaces = async () => {
      try {
        setLoading(true);
        const data = await touristPlaceService.getAllPlaces();
        setPlaces(data || []);
        setError('');
      } catch (requestError) {
        console.error('Places error:', requestError);
        setError(t('design.placeBrowse.errors.loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    fetchPlaces();
  }, [t]);

  const filteredPlaces = useMemo(() => {
    return places.filter((place) => {
      const haystack = `${place.name || ''} ${place.location || ''} ${place.description || ''}`.toLowerCase();
      return haystack.includes(query.toLowerCase());
    });
  }, [places, query]);

  if (loading) {
    return (
      <Layout>
        <LoadingState label={t('design.placeBrowse.loading')} />
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <ErrorState title={t('design.placeBrowse.unavailableTitle')} message={error} action={<button type="button" onClick={() => window.location.reload()} className="btn-primary">{t('common.tryAgain')}</button>} />
      </Layout>
    );
  }

  return (
    <Layout>
      <PageHero
        eyebrow={t('design.placeBrowse.heroEyebrow')}
        title={t('design.placeBrowse.heroTitle')}
        description={t('design.placeBrowse.heroDescription')}
        aside={
          <div className="soft-panel p-6 sm:p-8">
            <p className="eyebrow">{t('design.placeBrowse.searchEyebrow')}</p>
            <div className="relative mt-6">
              <FiSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t('design.placeBrowse.searchPlaceholder')}
                className="input-shell pl-11"
              />
            </div>
            <div className="mt-6 grid gap-3">
              <div className="rounded-[24px] border border-stone-200 bg-stone-50/80 p-4">
                <p className="text-xs uppercase tracking-[0.2em] text-stone-500">{t('design.placeBrowse.totalPlaces')}</p>
                <p className="mt-2 font-serif text-4xl font-semibold text-slate-900">{places.length}</p>
              </div>
              <div className="rounded-[24px] border border-stone-200 bg-stone-50/80 p-4">
                <p className="text-xs uppercase tracking-[0.2em] text-stone-500">{t('design.placeBrowse.searchStatus')}</p>
                <p className="mt-2 text-sm leading-7 text-slate-600">{t('design.placeBrowse.searchStatusDescription')}</p>
              </div>
            </div>
          </div>
        }
      />

      <section className="content-width pb-12">
        <SectionHeading
          eyebrow={t('design.placeBrowse.listEyebrow')}
          title={t('design.placeBrowse.listTitle', { count: filteredPlaces.length })}
          description={t('design.placeBrowse.listDescription')}
        />

        {filteredPlaces.length ? (
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {filteredPlaces.map((place) => (
              <Link key={place.id} to={`/place/${place.id}`} className="soft-card overflow-hidden">
                <div className="relative">
                  <img
                    src={place.image || '/place-placeholder.jpg'}
                    alt={place.name}
                    className="h-72 w-full object-cover"
                    onError={(event) => {
                      event.currentTarget.src = '/place-placeholder.jpg';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/72 via-slate-900/10 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                    <p className="mb-2 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/70">
                      <FiCompass />
                      {t('design.placeBrowse.locationPill')}
                    </p>
                    <h3 className="font-serif text-3xl font-semibold">{place.name}</h3>
                    <p className="mt-2 flex items-center gap-2 text-sm text-white/80">
                      <FiMapPin />
                      {place.location || t('design.footer.location')}
                    </p>
                  </div>
                </div>
                <div className="p-5">
                  <p className="line-clamp-3 text-sm leading-7 text-slate-600">{place.description || t('design.placeBrowse.descriptionFallback')}</p>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <EmptyState title={t('design.placeBrowse.noResultsTitle')} description={t('design.placeBrowse.noResultsDescription')} />
        )}
      </section>
    </Layout>
  );
};

export default TouristicPlacesPage;
